import { useParams, Link } from 'react-router-dom';
import { articles } from '@/mocks/articleData';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';
import { useMemo } from 'react';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = useMemo(() => {
    return articles.find((a) => a.slug === slug) || articles.find((a) => a.id === Number(slug));
  }, [slug]);

  if (!article) {
    return (
      <main>
        <div className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-background-100 rounded-2xl">
              <i className="ri-file-unknow-line text-2xl text-foreground-300"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">文章未找到</h1>
            <Link to="/" className="text-sm text-accent-600 hover:text-accent-700 cursor-pointer">
              返回首页
            </Link>
          </div>
        </div>
      </main>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    datePublished: article.date,
    image: article.image,
    author: {
      '@type': 'Organization',
      name: '福利视频网址大全',
    },
    publisher: {
      '@type': 'Organization',
      name: '福利视频网址大全',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `/article/${article.slug}`,
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '这些视频资源是否需要付费观看？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '我们收录的资源以免费高清资源为主，大部分内容无需付费即可观看。部分平台可能有少量付费内容，但我们会优先推荐免费资源。',
        },
      },
      {
        '@type': 'Question',
        name: '网站资源是否支持手机观看？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '是的，本站所有收录的视频资源均支持在手机、平板等移动设备上观看，适配各类屏幕尺寸。',
        },
      },
    ],
  };

  return (
    <>
      <SchemaOrg data={articleSchema} />
      <SchemaOrg data={faqSchema} />
      <main>
        <article>
          <div className="pt-20 pb-14 md:pb-20">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-4xl mx-auto">
                <Breadcrumb
                  items={[
                    { label: '首页', to: '/' },
                    { label: article.category, to: `/category/${article.categorySlug}` },
                    { label: article.title },
                  ]}
                />

                <div className="mt-8">
                  <div className="flex items-center gap-3 mb-4 flex-wrap">
                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-accent-100 text-accent-700 whitespace-nowrap">
                      {article.tag}
                    </span>
                    <Link
                      to={`/category/${article.categorySlug}`}
                      className="text-xs text-foreground-500 hover:text-accent-600 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      {article.category}
                    </Link>
                    <span className="text-xs text-foreground-400">
                      <i className="ri-calendar-line mr-1"></i>{article.date}
                    </span>
                    <span className="text-xs text-foreground-400">
                      <i className="ri-eye-line mr-1"></i>{article.views}
                    </span>
                  </div>

                  <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-6 leading-tight">
                    {article.title}
                  </h1>

                  <div className="w-full h-48 md:h-80 rounded-xl overflow-hidden mb-8 bg-background-200">
                    <img
                      src={article.image}
                      alt={article.title}
                      title={article.title}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  <div
                    className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: article.content }}
                  />

                  <div className="mt-12 pt-8 border-t border-background-200">
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                      <Link
                        to={`/category/${article.categorySlug}`}
                        className="text-sm text-accent-600 hover:text-accent-700 transition-colors cursor-pointer"
                      >
                        <i className="ri-arrow-left-line mr-1"></i>
                        返回{article.category}分类
                      </Link>

                      <div className="flex items-center gap-4">
                        <span className="text-xs text-foreground-400">分享到：</span>
                        <a
                          href="#"
                          className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-accent-100 hover:text-accent-600 transition-colors cursor-pointer"
                          aria-label="分享到微信"
                        >
                          <i className="ri-wechat-line"></i>
                        </a>
                        <a
                          href="#"
                          className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-accent-100 hover:text-accent-600 transition-colors cursor-pointer"
                          aria-label="分享到微博"
                        >
                          <i className="ri-weibo-line"></i>
                        </a>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 p-6 bg-background-100 rounded-xl">
                    <h3 className="font-heading text-base font-semibold text-foreground-900 mb-3">
                      相关推荐
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {articles
                        .filter((a) => a.categorySlug === article.categorySlug && a.id !== article.id)
                        .slice(0, 2)
                        .map((related) => (
                          <Link
                            key={related.id}
                            to={`/article/${related.slug}`}
                            className="flex items-start gap-3 p-3 rounded-lg hover:bg-background-50 transition-colors cursor-pointer"
                          >
                            <div className="shrink-0 w-16 h-12 rounded-md overflow-hidden bg-background-200">
                              <img
                                src={related.image}
                                alt={related.title}
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <div className="min-w-0">
                              <h4 className="text-sm font-medium text-foreground-800 line-clamp-2 leading-snug hover:text-accent-600 transition-colors">
                                {related.title}
                              </h4>
                              <span className="text-xs text-foreground-400 mt-1 block">
                                {related.date}
                              </span>
                            </div>
                          </Link>
                        ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </article>
      </main>
    </>
  );
}